import { useEffect } from "react";
import { cn } from "@apex/shared";

export interface RuleFiredToastProps {
  /** The message to display in the toast. */
  message: string;
  /** Called when the dismissal timer elapses. */
  onClose: () => void;
  /** Override the default 4-second dismissal window. */
  dismissAfterMs?: number;
  className?: string;
}

/**
 * RuleFiredToast — transient feedback when a compatibility rule fires
 * (auto-selection or auto-deselection driven by another item).
 *
 * Lives in `role="status"` with `aria-live="polite"` so screen readers
 * announce the rule effect without interrupting. Auto-dismisses after
 * 4 seconds by default; multiple toasts can stack via the parent container.
 */
export function RuleFiredToast({
  message,
  onClose,
  dismissAfterMs = 4000,
  className,
}: RuleFiredToastProps) {
  useEffect(() => {
    const t = setTimeout(onClose, dismissAfterMs);
    return () => clearTimeout(t);
  }, [onClose, dismissAfterMs]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "apex-pointer-events-auto apex-rounded-md apex-bg-foreground apex-text-background",
        "apex-px-3 apex-py-2 apex-text-sm apex-shadow-lg",
        "apex-flex apex-items-center apex-gap-2 apex-min-w-[220px] apex-max-w-[400px]",
        className,
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="apex-shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
      <span className="apex-flex-1">{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="apex-shrink-0 apex-rounded apex-p-0.5 hover:apex-opacity-80 apex-transition-opacity"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
  );
}
